import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography } from '@material-ui/core';
import RoomRoundedIcon from '@material-ui/icons/RoomRounded';
import Highcharts from 'highcharts/highmaps';
import HighchartsReact from 'highcharts-react-official';
import proj4 from 'proj4';
import world from '@highcharts/map-collection/custom/world.geo.json';
import NoDataCard from './NoDataCard';
import CardSkeleton from './CardSkeleton';

const getBubbleSize = (count) => {
  if (count >= 21) return 16;
  if (count >= 11) return 13.5;
  if (count >= 6) return 11;
  return 9;
};

const getBubbleColor = (count) => {
  if (count >= 21) return '#0D366B';
  if (count >= 11) return '#1C5CAB';
  if (count >= 6) return '#3987E5';
  return '#86B6EF';
};

const GraphCard = ({ cardName = 'Assets by Location', cardData, loading }) => {
  const locations = useMemo(
    () => (cardData || []).filter((location) => location?.latitude != null && location?.longitude != null),
    [cardData]
  );

  const bubbleData = useMemo(
    () => locations.map((location) => ({
      name: location?.label,
      lat: location.latitude,
      lon: location.longitude,
      count: location?.count || 0,
      marker: { radius: getBubbleSize(location?.count || 0) },
      color: getBubbleColor(location?.count || 0),
    })),
    [locations]
  );

  const options = useMemo(() => ({
    chart: {
      map: world,
      proj4,
      backgroundColor: 'transparent',
      height: 300,
      spacing: [0, 0, 0, 0],
      animation: false,
    },
    title: { text: null },
    credits: { enabled: false },
    mapNavigation: {
      enabled: true,
      buttonOptions: { verticalAlign:'top' },
    },
    tooltip: {
      useHTML: true,
      backgroundColor: '#FFFFFF',
      borderColor: '#DCE4ED',
      borderRadius: 10,
      shadow: false,
      formatter: function formatter() {
        return `<div style="padding:4px 6px;max-width:220px;white-space:normal;">
            <div style="font-size:12px;color:#68758A;margin-bottom:4px;">${this.point.name}</div>
            <div style="font-size:18px;font-weight:700;color:#101828;">${this.point.count}
              <span style="font-size:11px;font-weight:400;color:#667085;">assets</span>
            </div>
          </div>`;
      },
    },
    plotOptions: {
      series: { animation: false },
      map: {
        enableMouseTracking: false,
        borderColor: '#FFFFFF',
        borderWidth: 0.6,
        color: '#E7EDF3',
      },
      mappoint: {
        marker: {
          symbol: 'circle',
          lineColor: '#FFFFFF',
          lineWidth: 2,
        },
        states: { hover: { brightness: 0.08 } },
      },
    },
    series: [
      {
        type: 'map',
        name: 'World',
        mapData: world,
        data: [],
        allAreas: true,
        nullColor: '#E7EDF3',
        showInLegend: false,
      },
      {
        type: 'mappoint',
        name: 'Locations',
        data: bubbleData,
      },
    ],
  }), [bubbleData]);

  if (loading) {
    return <CardSkeleton fullHeight />;
  }

  if (!bubbleData.length) {
    return <NoDataCard title={cardName} />;
  }

  return (
    <Card style={{ width: '100%', border: '1px solid #E5E7EB', borderRadius: 10, backgroundColor: '#FFFFFF', overflow: 'hidden', boxShadow: '0 4px 18px rgba(15, 23, 42, 0.05)' }} elevation={1}>
      <Box style={{ padding: '14px 18px', borderBottom: '1px solid #EEF0F4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Box style={{ width: 20, height: 20, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #EEF3FB 0%, #CDE2FB 100%)', color: '#1C5CAB', marginRight: 8 }}>
            <RoomRoundedIcon fontSize="small" />
          </Box>
          <Typography style={{ fontSize: 16, fontWeight: 800, color: '#172033', lineHeight: 1.2 }}>{cardName}</Typography>
        </Box>
      </Box>

      <CardContent style={{ padding: '5px 12px 12px', position: 'relative' }}>
        <HighchartsReact
          highcharts={Highcharts}
          constructorType="mapChart"
          options={options}
        />

        <Box style={{ position: 'absolute', left: 22, bottom: 14, padding: '10px 14px', background: 'rgba(255,255,255,0.96)', border: '1px solid #DCE4ED', borderRadius: 8, boxShadow: '0 2px 8px rgba(16,24,40,0.06)' }}>
          <Typography style={{ fontSize: 12, fontWeight: 600, color: '#172B4D', marginBottom: 8 }}>Asset count</Typography>
          <Box style={{ display: 'flex', alignItems: 'flex-end', gap: 14 }}>
            {[{ label: '1 - 5', size: 12, color: '#86B6EF' }, { label: '6 - 10', size: 16, color: '#3987E5' }, { label: '11 - 20', size: 20, color: '#1C5CAB' }, { label: '21+', size: 24, color: '#0D366B' }].map((item) => (
              <Box key={item.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, color: '#667085', fontSize: 11 }}>
                <span style={{ display: 'block', width: item.size, height: item.size, borderRadius: '50%', background: item.color }} />
                <span>{item.label}</span>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GraphCard;
